import type { OutreachContactDecision } from '@workspace/api-backend'
import type {
  AdminCallCandidateItem,
  AdminLimitsPendingItem,
  AdminOutreachStartupMetricsItem,
  AdminOutreachVariants,
  AdminPendingOutreachItem,
  AdminSeatQueueResponse,
  AdminStuckAwaitingSenderItem,
  AdminWithdrawalRequestItem,
  OutreachArmReadoutData,
  OutreachHeyReachDebug,
  OutreachMessagesData,
  OutreachQueueItem,
  OutreachStatusData,
  PortfolioMessagesData,
} from './types'

export type { OutreachContactDecision }

const BASE = import.meta.env.VITE_BACKEND_URL

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined && init.body !== null
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`${res.status} ${res.statusText}: ${text}`)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

function unwrap<T>(payload: { status?: string; data?: T } | T): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export const outreachApi = {
  getStatus: async (startupId: string): Promise<OutreachStatusData> => {
    const res = await request<{ status: string; data: OutreachStatusData } | OutreachStatusData>(
      `/api/v2/startups/${startupId}/outreach`
    )
    return unwrap(res)
  },
  enable: async (startupId: string): Promise<OutreachStatusData> => {
    const res = await request<{ status: string; data: OutreachStatusData } | OutreachStatusData>(
      `/api/v2/startups/${startupId}/outreach/enable`,
      { method: 'POST' }
    )
    return unwrap(res)
  },
  disable: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/disable`, { method: 'POST' }),
  adminCopyArms: async (): Promise<OutreachArmReadoutData> => {
    const res = await request<
      { status: string; data: OutreachArmReadoutData } | OutreachArmReadoutData
    >('/api/v2/admin/outreach/copy-arms')
    return unwrap(res)
  },

  getMessages: async (startupId: string, backfillArms = false): Promise<OutreachMessagesData> => {
    const res = await request<
      { status: string; data: OutreachMessagesData } | OutreachMessagesData
    >(`/api/v2/startups/${startupId}/outreach/messages${backfillArms ? '?backfillArms=true' : ''}`)
    return unwrap(res)
  },
  updateMessages: async (
    startupId: string,
    body: { msg1?: string; msg2?: string; msg3?: string }
  ): Promise<OutreachMessagesData> => {
    const res = await request<
      { status: string; data: OutreachMessagesData } | OutreachMessagesData
    >(`/api/v2/startups/${startupId}/outreach/messages`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
    return unwrap(res)
  },
  /** Drop the founder edit for one field and go back to the generated copy. */
  resetMessage: async (
    startupId: string,
    field: 'msg1' | 'msg2' | 'msg3'
  ): Promise<OutreachMessagesData> => {
    const res = await request<
      { status: string; data: OutreachMessagesData } | OutreachMessagesData
    >(`/api/v2/startups/${startupId}/outreach/messages/${field}/custom`, { method: 'DELETE' })
    return unwrap(res)
  },
  reviewMessages: async (startupId: string): Promise<OutreachMessagesData> => {
    const res = await request<
      { status: string; data: OutreachMessagesData } | OutreachMessagesData
    >(`/api/v2/startups/${startupId}/outreach/messages/review`, { method: 'POST' })
    return unwrap(res)
  },
  launch: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/launch`, { method: 'POST' }),
  getContactDecisions: async (startupId: string): Promise<OutreachContactDecision[]> => {
    const res = await request<{ data: { items: OutreachContactDecision[] } }>(
      `/api/v2/startups/${startupId}/outreach/contact-decisions`
    )
    return res.data?.items ?? []
  },
  setContactDecisions: async (
    startupId: string,
    decisions: OutreachContactDecision[]
  ): Promise<OutreachContactDecision[]> => {
    const res = await request<{ data: { items: OutreachContactDecision[] } }>(
      `/api/v2/startups/${startupId}/outreach/contact-decisions`,
      { method: 'POST', body: JSON.stringify({ decisions }) }
    )
    return res.data?.items ?? []
  },
  requestWithdrawal: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/wallet/request-withdrawal`, {
      method: 'POST',
    }),
  requestDisconnect: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/request-disconnect`, {
      method: 'POST',
    }),
  getQueue: async (startupId: string): Promise<OutreachQueueItem[]> => {
    const res = await request<{ data: { items: OutreachQueueItem[] } }>(
      `/api/v2/startups/${startupId}/outreach/queue`
    )
    return res.data?.items ?? []
  },
  approveQueued: (startupId: string, leadId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/queue/${leadId}/approve`, {
      method: 'POST',
    }),
  approveAllQueued: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/queue/approve-all`, {
      method: 'POST',
    }),
  skipAllQueued: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/queue/skip-all`, {
      method: 'POST',
    }),
  declineQueued: (startupId: string, leadId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/queue/${leadId}/decline`, {
      method: 'POST',
    }),
  archiveContact: (startupId: string, leadId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/queue/${leadId}/archive`, {
      method: 'POST',
    }),
  removeInProgressLead: (startupId: string, leadId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/queue/${leadId}/remove`, {
      method: 'POST',
    }),
  getArchive: async (startupId: string): Promise<OutreachQueueItem[]> => {
    const res = await request<{ data: { items: OutreachQueueItem[] } }>(
      `/api/v2/startups/${startupId}/outreach/archive`
    )
    return res.data?.items ?? []
  },
  restoreArchived: (startupId: string, leadId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/archive/${leadId}/restore`, {
      method: 'POST',
    }),

  adminPending: async (): Promise<AdminPendingOutreachItem[]> => {
    const res = await request<{ data: { items: AdminPendingOutreachItem[] } }>(
      '/api/v2/admin/outreach/pending'
    )
    return res.data?.items ?? []
  },
  adminLimitsPending: async (): Promise<AdminLimitsPendingItem[]> => {
    const res = await request<{ data: { items: AdminLimitsPendingItem[] } }>(
      '/api/v2/admin/outreach/limits-pending'
    )
    return res.data?.items ?? []
  },
  adminMarkLimitsSet: (id: string) =>
    request<unknown>(`/api/v2/admin/outreach/${id}/limits-set`, { method: 'POST' }),
  adminWithdrawalRequests: async (): Promise<AdminWithdrawalRequestItem[]> => {
    const res = await request<{ data: { items: AdminWithdrawalRequestItem[] } }>(
      '/api/v2/admin/outreach/withdrawal-requests'
    )
    return res.data?.items ?? []
  },
  adminResolveWithdrawal: (id: string) =>
    request<unknown>(`/api/v2/admin/outreach/withdrawal-requests/${id}/resolve`, {
      method: 'POST',
    }),
  adminSeatQueue: async (): Promise<AdminSeatQueueResponse> => {
    const res = await request<{ data: AdminSeatQueueResponse }>('/api/v2/admin/outreach/seat-queue')
    return res.data ?? { activeItems: [], activeCount: 0 }
  },
  adminCallCandidates: async (): Promise<AdminCallCandidateItem[]> => {
    const res = await request<{ data: { items: AdminCallCandidateItem[] } }>(
      '/api/v2/admin/outreach/call-candidates'
    )
    return res.data?.items ?? []
  },
  /** Every live message variant for a startup, both campaigns. Admin only. */
  adminGetVariants: async (startupId: string): Promise<AdminOutreachVariants> => {
    const res = await request<
      { status: string; data: AdminOutreachVariants } | AdminOutreachVariants
    >(`/api/v2/admin/startups/${startupId}/outreach/variants`)
    return unwrap(res)
  },
  adminGetStatus: async (startupId: string): Promise<OutreachStatusData> => {
    const res = await request<{ status: string; data: OutreachStatusData } | OutreachStatusData>(
      `/api/v2/admin/startups/${startupId}/outreach`
    )
    return unwrap(res)
  },
  adminGetHeyReachDebug: async (startupId: string): Promise<OutreachHeyReachDebug> => {
    const res = await request<
      { status: string; data: OutreachHeyReachDebug } | OutreachHeyReachDebug
    >(`/api/v2/admin/startups/${startupId}/outreach/heyreach`)
    return unwrap(res)
  },
  adminRecheck: (id: string) =>
    request<unknown>(`/api/v2/admin/outreach/${id}/recheck`, { method: 'POST' }),
  adminEject: (id: string) =>
    request<unknown>(`/api/v2/admin/outreach/${id}/eject`, { method: 'POST' }),
  adminStartupMetrics: async (): Promise<AdminOutreachStartupMetricsItem[]> => {
    const res = await request<{ data: { items: AdminOutreachStartupMetricsItem[] } }>(
      '/api/v2/admin/outreach/startup-metrics'
    )
    return res.data?.items ?? []
  },
  adminStuckSenders: async (): Promise<AdminStuckAwaitingSenderItem[]> => {
    const res = await request<{ data: { items: AdminStuckAwaitingSenderItem[] } }>(
      '/api/v2/admin/outreach/stuck-senders'
    )
    return res.data?.items ?? []
  },
  adminResetStuckWorkspace: (id: string) =>
    request<unknown>(`/api/v2/admin/outreach/${id}/workspace/reset`, { method: 'POST' }),
  adminForceDisconnect: (startupId: string) =>
    request<unknown>(`/api/v2/admin/startups/${startupId}/outreach/force-disconnect`, {
      method: 'POST',
    }),
  adminCallDecision: (messageId: string, decision: 'confirm' | 'dismiss') =>
    request<unknown>(`/api/v2/admin/outreach/messages/${messageId}/call-decision`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    }),

  // ── Portfolio outreach ──────────────────────────────────────────────────────

  portfolioGetContacts: async (
    startupId: string
  ): Promise<
    Array<{
      contactId: string
      name: string | null
      title: string | null
      linkedinUrl: string
      companyName: string
      fundName: string
      decision: 'APPROVED' | 'DECLINED' | null
    }>
  > => {
    const res = await request<{
      data: Array<{
        contactId: string
        name: string | null
        title: string | null
        linkedinUrl: string
        companyName: string
        fundName: string
        decision: 'APPROVED' | 'DECLINED' | null
      }>
    }>(`/api/v2/startups/${startupId}/outreach/portfolio/contacts`)
    return res.data ?? []
  },
  portfolioSetContactDecisions: (
    startupId: string,
    decisions: Array<{ contactId: string; decision: 'APPROVED' | 'DECLINED' }>
  ) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/portfolio/contacts/decisions`, {
      method: 'POST',
      body: JSON.stringify({ decisions }),
    }),
  portfolioApproveContacts: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/portfolio/contacts/approve`, {
      method: 'POST',
    }),
  portfolioGetMessages: async (
    startupId: string,
    backfillArms = false
  ): Promise<PortfolioMessagesData> => {
    const res = await request<{ data: PortfolioMessagesData }>(
      `/api/v2/startups/${startupId}/outreach/portfolio/messages${
        backfillArms ? '?backfillArms=true' : ''
      }`
    )
    return res.data
  },
  portfolioUpdateMessages: (
    startupId: string,
    body: { msg1?: string | null; msg2?: string | null; msg3?: string | null }
  ) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/portfolio/messages`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /** Drop the founder edit for one portfolio message and use the generated copy. */
  portfolioResetMessage: (startupId: string, field: 'msg1' | 'msg2' | 'msg3') =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/portfolio/messages/${field}/custom`, {
      method: 'DELETE',
    }),
  portfolioReviewMessages: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/portfolio/messages/review`, {
      method: 'POST',
    }),
  portfolioLaunch: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/portfolio/launch`, {
      method: 'POST',
    }),

  adminWarmIntroCandidates: async (): Promise<AdminWarmIntroCandidate[]> => {
    const res = await request<{ data: { items: AdminWarmIntroCandidate[] } }>(
      '/api/v2/admin/outreach/warm-intro-candidates'
    )
    return res.data?.items ?? []
  },
  adminWarmIntroDecision: (messageId: string, startupId: string, decision: 'CONFIRM' | 'DISMISS') =>
    request<unknown>(
      `/api/v2/startups/${startupId}/outreach/portfolio/queue/${messageId}/warm-intro`,
      { method: 'POST', body: JSON.stringify({ decision }) }
    ),
  skipAllPortfolioQueued: (startupId: string) =>
    request<unknown>(`/api/v2/startups/${startupId}/outreach/portfolio/queue/skip-all`, {
      method: 'POST',
    }),
  archivePortfolioMessage: (startupId: string, messageId: string) =>
    request<unknown>(
      `/api/v2/startups/${startupId}/outreach/portfolio/queue/${messageId}/archive`,
      { method: 'POST' }
    ),
}

export type AdminWarmIntroCandidate = {
  messageId: string
  startupId: string
  startupName: string
  founderName: string | null
  founderTitle: string | null
  companyName: string
  fundName: string
  replySummary: string | null
  workspaceId: number | null
  workspaceName: string
}

export const portfolioContactsQueryKey = (startupId: string) =>
  ['v2', 'startups', startupId, 'outreach', 'portfolio', 'contacts'] as const
export const portfolioMessagesQueryKey = (startupId: string) =>
  ['v2', 'startups', startupId, 'outreach', 'portfolio', 'messages'] as const
export const adminWarmIntroCandidatesQueryKey = () =>
  ['v2', 'admin', 'outreach', 'warm-intro-candidates'] as const

export const outreachStatusQueryKey = (startupId: string) =>
  ['v2', 'startups', startupId, 'outreach'] as const
/**
 * `backfillArms` is part of the key so the dialog's generating fetch is a
 * separate cache entry from the page's plain read. Invalidation still hits both:
 * TanStack matches query keys by PREFIX, so the base key covers either form.
 */
export const outreachMessagesQueryKey = (startupId: string, backfillArms = false) =>
  backfillArms
    ? (['v2', 'startups', startupId, 'outreach', 'messages', 'backfill'] as const)
    : (['v2', 'startups', startupId, 'outreach', 'messages'] as const)
export const outreachQueueQueryKey = (startupId: string) =>
  ['v2', 'startups', startupId, 'outreach', 'queue'] as const
export const outreachContactDecisionsQueryKey = (startupId: string) =>
  ['v2', 'startups', startupId, 'outreach', 'contact-decisions'] as const
export const outreachArchiveQueryKey = (startupId: string) =>
  ['v2', 'startups', startupId, 'outreach', 'archive'] as const
export const adminPendingQueryKey = () => ['v2', 'admin', 'outreach', 'pending'] as const
export const adminLimitsPendingQueryKey = () =>
  ['v2', 'admin', 'outreach', 'limits-pending'] as const
export const adminSeatQueueQueryKey = () => ['v2', 'admin', 'outreach', 'seat-queue'] as const
export const adminCallCandidatesQueryKey = () =>
  ['v2', 'admin', 'outreach', 'call-candidates'] as const
export const adminWithdrawalRequestsQueryKey = () =>
  ['v2', 'admin', 'outreach', 'withdrawal-requests'] as const
export const adminStuckSendersQueryKey = () => ['v2', 'admin', 'outreach', 'stuck-senders'] as const
export const adminStartupMetricsQueryKey = () =>
  ['v2', 'admin', 'outreach', 'startup-metrics'] as const
